/**
 * Waehrungs-/Einheitenumrechnung fuer die Charts.
 *
 * Gerechnet wird ausschliesslich im Backend (chart/series, chart/compare,
 * chart/export mit ?currency=&unit=), Tag fuer Tag mit dem Kurs des jeweiligen
 * Tages. Hier liegen nur die Bausteine fuer die Oberflaeche: Auswahllisten aus
 * /reference/currencies und /reference/units, Request-Parameter und die
 * Beschriftung anhand von meta aus der Antwort.
 */
import { formatCurrency, formatUnit } from './formatUnit';

// Kurze Liste oben im Waehrungs-Dropdown, danach der Rest nach sort_idx.
export const TOP_CURRENCIES = ['USD', 'EUR', 'CNY', 'GBP', 'JPY', 'CHF'];

const trim = (v) => String(v ?? '').trim();
const lower = (v) => trim(v).toLowerCase();

/** Rohen Einheitencode behalten, aber "n/a", "unit" usw. als leer behandeln. */
export const cleanUnitCode = (unit) => (formatUnit(unit) ? trim(unit) : '');

const langOf = (language) => lower(language).slice(0, 2);

/** Name in der UI-Sprache (de/en/zh), sonst Englisch, sonst der Code. */
export function localizedName(entry, language) {
  if (!entry) return '';
  const lang = langOf(language);
  const byLang = { de: entry.name_de, en: entry.name_en, zh: entry.name_zh };
  return trim(byLang[lang]) || trim(entry.name_en) || trim(entry.name_de) || trim(entry.name_zh) || trim(entry.code);
}

const sortIdx = (entry) => {
  const n = Number(entry?.sort_idx);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
};

const bySortIdx = (a, b) => sortIdx(a) - sortIdx(b) || trim(a.code).localeCompare(trim(b.code));

const findByCode = (list, code) => {
  const key = lower(code);
  if (!key || !Array.isArray(list)) return null;
  return list.find((e) => trim(e?.code) === trim(code)) || list.find((e) => lower(e?.code) === key) || null;
};

const hasToBase = (u) => u?.to_base !== null && u?.to_base !== undefined && Number.isFinite(Number(u.to_base)) && Number(u.to_base) > 0;

/**
 * Auswahl fuer das Waehrungs-Dropdown einer Reihe.
 *
 * Returns { disabled, hint, options } mit
 *   options[0] = { value: null, isOriginal: true, code: <Quellwaehrung> }
 *   weitere    = { value: 'EUR', code, short: 'EUR', label: 'EUR – Euro', group: 'top' | 'rest' }
 * hint (i18n-Schluesselsuffix): 'noCurrency' | 'currencyNotConvertible' | 'referenceUnavailable' | null
 */
export function buildCurrencyOptions({ currencies, sourceCurrency, language } = {}) {
  const source = formatCurrency(sourceCurrency);
  const original = { value: null, isOriginal: true, code: source };
  if (!source) return { disabled: true, hint: 'noCurrency', options: [] };
  if (!Array.isArray(currencies) || currencies.length === 0) {
    return { disabled: true, hint: 'referenceUnavailable', options: [original] };
  }
  const sourceEntry = findByCode(currencies, source);
  if (!sourceEntry || sourceEntry.convertible !== true) {
    return { disabled: true, hint: 'currencyNotConvertible', options: [original] };
  }
  const candidates = currencies.filter(
    (c) => c && trim(c.code) && c.convertible === true && c.visible !== false && lower(c.code) !== lower(sourceEntry.code)
  );
  const top = TOP_CURRENCIES
    .map((code) => candidates.find((c) => trim(c.code).toUpperCase() === code))
    .filter(Boolean);
  const rest = candidates.filter((c) => !top.includes(c)).sort(bySortIdx);
  const toOption = (group) => (c) => {
    const code = trim(c.code);
    const name = localizedName(c, language);
    return { value: code, code, short: code, label: name && name !== code ? `${code} – ${name}` : code, group };
  };
  return {
    disabled: false,
    hint: null,
    options: [original, ...top.map(toOption('top')), ...rest.map(toOption('rest'))],
  };
}

/**
 * Auswahl fuer das Einheiten-Dropdown: nur Einheiten derselben Dimension
 * (Masse, Volumen, Energie ...) mit convertible=true und gesetztem to_base.
 * Gilt auch fuer Reihen ohne Waehrung (z. B. Gasspeicher in GWh -> TWh/MWh,
 * Volumen in m³ -> l); das Backend rechnet die Einheit dann allein um.
 * %, Index und Einheiten ohne to_base bleiben gesperrt.
 *
 * hint: 'noUnit' | 'unitNotConvertible' | 'referenceUnavailable' | null
 */
export function buildUnitOptions({ units, sourceUnit, language } = {}) {
  const source = cleanUnitCode(sourceUnit);
  const original = { value: null, isOriginal: true, code: source };
  if (!source) return { disabled: true, hint: 'noUnit', options: [] };
  if (!Array.isArray(units) || units.length === 0) {
    return { disabled: true, hint: 'referenceUnavailable', options: [original] };
  }
  const sourceEntry = findByCode(units, source);
  if (!sourceEntry || sourceEntry.convertible !== true || !trim(sourceEntry.dimension) || !hasToBase(sourceEntry)) {
    return { disabled: true, hint: 'unitNotConvertible', options: [original] };
  }
  const dimension = lower(sourceEntry.dimension);
  const options = units
    .filter((u) => u && trim(u.code)
      && u.convertible === true
      && hasToBase(u)
      && lower(u.dimension) === dimension
      && lower(u.code) !== lower(sourceEntry.code))
    .sort((a, b) => Number(a.to_base) - Number(b.to_base) || trim(a.code).localeCompare(trim(b.code)))
    .map((u) => {
      const code = trim(u.code);
      const short = trim(u.symbol) || formatUnit(code);
      const name = localizedName(u, language);
      return { value: code, code, short, label: name && name !== short ? `${short} – ${name}` : short };
    });
  if (!options.length) return { disabled: true, hint: 'unitNotConvertible', options: [original] };
  return { disabled: false, hint: null, options: [original, ...options] };
}

/**
 * Ziel einer Reihe aus den Chart-Einstellungen: { currency, unit }, jeweils
 * null fuer "Original". Eine Auswahl gleich der Quelle gilt als Original.
 */
export function conversionTarget(setting) {
  if (!setting) return { currency: null, unit: null };
  const cur = formatCurrency(setting.targetCurrency);
  const unit = cleanUnitCode(setting.targetUnit);
  return {
    currency: cur && cur !== formatCurrency(setting.baseCurrency) ? trim(setting.targetCurrency) : null,
    unit: unit && lower(unit) !== lower(setting.baseUnit) ? unit : null,
  };
}

/** Query-Parameter fuer chart/series|compare|export (fehlende Werte entfallen). */
export function conversionParams(target) {
  const params = {};
  if (target?.currency) params.currency = target.currency;
  if (target?.unit) params.unit = target.unit;
  return params;
}

/** Stabiler Schluessel fuer Caches/Effekte, "" = keine Umrechnung. */
export function conversionKey(target) {
  if (!target?.currency && !target?.unit) return '';
  return `${target.currency || ''}|${target.unit || ''}`;
}

export const hasConversionTarget = (target) => Boolean(target?.currency || target?.unit);

/**
 * Vergleichsreihen nach Ziel gruppieren: chart/compare kennt nur ein Ziel je
 * Request. Ohne individuelle Einstellungen bleibt es bei einem Request.
 * Returns Array<{ codes: string[], target, params }>
 */
export function groupComparisonRequests(comparisons = [], targetFor = () => null) {
  const groups = new Map();
  comparisons.forEach((comparison) => {
    const code = comparison?.code;
    if (!code) return;
    const target = targetFor(comparison) || { currency: null, unit: null };
    const key = conversionKey(target);
    if (!groups.has(key)) groups.set(key, { codes: [], target, params: conversionParams(target) });
    const group = groups.get(key);
    if (!group.codes.includes(code)) group.codes.push(code);
  });
  return Array.from(groups.values());
}

/**
 * Beschriftung einer Reihe aus meta der Antwort. Waehrung/Einheit wechseln nur,
 * wenn das Backend tatsaechlich umgerechnet hat; sonst bleiben die Originalwerte
 * (die Daten sind dann ebenfalls unveraendert).
 *
 * Returns { currency, unit, sourceCurrency, sourceUnit, converted,
 *           currencyConverted, unitConverted, requested, failed, reason }
 */
export function resolveSeriesDisplay({ sourceCurrency, sourceUnit, target, meta } = {}) {
  const srcCurrency = formatCurrency(meta?.source_currency) || formatCurrency(sourceCurrency);
  const srcUnit = cleanUnitCode(meta?.source_unit) || cleanUnitCode(sourceUnit);
  const requestedCurrency = Boolean(target?.currency);
  const requestedUnit = Boolean(target?.unit);
  const requested = requestedCurrency || requestedUnit;

  const converted = Boolean(meta && meta.converted);
  const currencyConverted = converted && Boolean(meta.currency_converted ?? (formatCurrency(meta.currency) && formatCurrency(meta.currency) !== srcCurrency));
  const unitConverted = converted && Boolean(meta.unit_converted ?? (cleanUnitCode(meta.unit) && lower(meta.unit) !== lower(srcUnit)));

  const currency = currencyConverted ? formatCurrency(meta.currency) : srcCurrency;
  const unit = unitConverted ? cleanUnitCode(meta.unit) : srcUnit;

  // Angefragt, aber (ganz oder teilweise) nicht umgerechnet.
  const failed = requested && (!converted || (requestedCurrency && !currencyConverted) || (requestedUnit && !unitConverted));
  const reason = failed ? (trim(meta?.reason) || null) : null;

  return {
    currency,
    unit,
    sourceCurrency: srcCurrency,
    sourceUnit: srcUnit,
    converted: currencyConverted || unitConverted,
    currencyConverted,
    unitConverted,
    requested,
    failed,
    reason,
  };
}

const describeFxSeries = (entry) => {
  if (entry === null || entry === undefined) return '';
  if (typeof entry !== 'object') return trim(entry);
  const pair = entry.pair || (entry.from && entry.to ? `${entry.from}/${entry.to}` : null)
    || (entry.base && entry.quote ? `${entry.base}/${entry.quote}` : null);
  const parts = [
    entry.label || entry.name || pair,
    entry.series || entry.series_key || entry.key || entry.code || entry.id,
    entry.source,
  ].map(trim).filter(Boolean);
  return Array.from(new Set(parts)).join(' · ');
};

/**
 * Hinweis unter dem Chart.
 * Returns null oder { kind: 'ecb' | 'fx' | 'unit', basis, fxSeries: string[], skipped }
 */
export function conversionNote(meta, display) {
  if (!display?.converted) return null;
  const skipped = Math.max(0, Number(meta?.points_without_rate) || 0);
  if (!display.currencyConverted) return { kind: 'unit', basis: '', fxSeries: [], skipped };
  const basis = trim(meta?.fx_basis);
  const fxSeries = (Array.isArray(meta?.fx_series) ? meta.fx_series : [])
    .map(describeFxSeries)
    .filter(Boolean);
  const isEcb = /ecb|ezb/i.test(basis) || (!basis && fxSeries.some((s) => /^EXR\./.test(s)));
  return { kind: isEcb ? 'ecb' : 'fx', basis, fxSeries, skipped };
}

/**
 * Letzter und vorletzter Kurs einer (umgerechneten) Reihe fuer die Kopfzeilen.
 * 0 gilt wie im Backend als Luecke, nicht als Kurs.
 * Returns null oder { value, previous, absChange, pctChange, date }
 */
export function latestChangeFromSeries(points = [], now = Date.now()) {
  if (!Array.isArray(points)) return null;
  const valid = points
    .map((p) => ({ date: p?.date, value: Number(p?.close ?? p?.value) }))
    .filter((p) => {
      const ts = p.date ? new Date(p.date).getTime() : NaN;
      return Number.isFinite(ts) && ts <= now && Number.isFinite(p.value) && p.value !== 0;
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  if (!valid.length) return null;
  const last = valid[valid.length - 1];
  const prev = valid.length > 1 ? valid[valid.length - 2] : null;
  const absChange = prev ? last.value - prev.value : null;
  const pctChange = prev && prev.value !== 0 ? (absChange / prev.value) * 100 : null;
  return { value: last.value, previous: prev ? prev.value : null, absChange, pctChange, date: last.date };
}
